import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

const MAX_POINTS = 4;

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white px-4 py-2 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
const labelCls = "block text-sm font-['Manrope'] font-medium text-white/80 mb-2";

/**
 * Admin editor for up to 4 vehicle description bullet points.
 * points: string[]
 * onChange(nextPoints)
 */
export default function DescriptionPointsEditor({
  points = [],
  onChange,
  disabled = false,
}) {
  const list = Array.isArray(points) ? points.slice(0, MAX_POINTS) : [];

  const setAt = (index, value) => {
    const next = [...list];
    next[index] = value;
    if (typeof onChange === 'function') onChange(next);
  };

  const removeAt = (index) => {
    if (typeof onChange === 'function') {
      onChange(list.filter((_, i) => i !== index));
    }
  };

  const addPoint = () => {
    if (list.length >= MAX_POINTS || disabled) return;
    if (typeof onChange === 'function') onChange([...list, '']);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <label className={labelCls}>Vehicle Description Points</label>
          <p className="text-xs text-white/40 font-['Manrope'] -mt-1">
            Optional. Up to {MAX_POINTS} English bullet points shown when customers click
            &quot;View Vehicle Description&quot;. Spanish is generated automatically on save.
          </p>
        </div>
        <button
          type="button"
          disabled={disabled || list.length >= MAX_POINTS}
          onClick={addPoint}
          className="inline-flex items-center gap-2 px-3 py-2 bg-blue-500/20 border border-blue-500/40 text-blue-300 hover:bg-blue-500/30 transition-colors font-['Manrope'] text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add point
        </button>
      </div>

      {list.length === 0 ? (
        <button
          type="button"
          disabled={disabled}
          onClick={addPoint}
          className="w-full border border-dashed border-white/15 bg-white/[0.03] hover:border-blue-500/40 hover:bg-blue-500/5 transition-colors py-6 px-4 text-center disabled:opacity-50"
        >
          <p className="text-sm text-white/50 font-['Manrope']">
            No description points yet — click to add the first
          </p>
        </button>
      ) : (
        <div className="space-y-2">
          {list.map((point, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="mt-2.5 text-xs text-white/40 font-['Manrope'] w-5 shrink-0">
                {index + 1}.
              </span>
              <input
                type="text"
                value={point}
                disabled={disabled}
                onChange={(e) => setAt(index, e.target.value)}
                className={inputCls}
                placeholder={`Description point ${index + 1}`}
                maxLength={280}
              />
              <button
                type="button"
                title="Remove"
                disabled={disabled}
                onClick={() => removeAt(index)}
                className="mt-1 p-2 bg-white/5 border border-white/10 text-white/70 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <p className="text-xs text-white/40 font-['Manrope']">
          {list.length} of {MAX_POINTS} points
        </p>
      )}
    </div>
  );
}

export function cleanDescriptionPoints(points = []) {
  return (Array.isArray(points) ? points : [])
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean)
    .slice(0, MAX_POINTS);
}
